import React, { useEffect, useState } from 'react';
import { nacService, NacExceptionItem } from '../../services/nac.service';
import { useTableSort, sortIndicator } from '../../lib/tableSort';

/**
 * IPT-556 이슈3 — NAC 운영보고서 예외처리 이력 (read-only)
 *
 * - 보고서 상단(1차)에서 선택한 회사의 전체 예외처리 항목을 기간 무관하게 표시
 * - viewer 접근 가능한 /nac/report/exceptions(PermissionGuard) 사용
 * - 자체 계열사 필터(2차) 없음, 관리(CRUD) 없음 — 조회 전용
 * - 컬럼 헤더 클릭 정렬(lib/tableSort)
 */
interface Props {
  company: string;
}

const fmt = (d?: string | null) => (d ? d.slice(0, 10) : '-');

const NacExceptionHistory: React.FC<Props> = ({ company }) => {
  const [rows, setRows] = useState<NacExceptionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { sorted, sortKey, sortDir, onSort } = useTableSort<NacExceptionItem>(rows);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!company) {
        setRows([]);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const data = await nacService.getReportExceptions(company);
        if (!cancelled) setRows(data);
      } catch {
        if (!cancelled) {
          setError('예외처리 이력을 불러오는 데 실패했습니다.');
          setRows([]); // 실패 시 이전 회사 데이터가 남지 않도록 초기화
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [company]);

  const th = (key: keyof NacExceptionItem, label: string) => (
    <th
      onClick={() => onSort(key)}
      style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', color: sortKey === key ? '#1a73e8' : undefined }}
    >
      {label} <span style={{ fontSize: 11, color: sortKey === key ? '#1a73e8' : '#bbb' }}>{sortIndicator(sortKey, key, sortDir)}</span>
    </th>
  );

  if (!company) {
    return <div style={{ padding: 24, color: '#888' }}>회사를 먼저 선택하세요.</div>;
  }

  return (
    <div className="nac-exception-history">
      {error && <div className="alert-message error" style={{ marginBottom: 12 }}>{error}</div>}
      <div style={{ marginBottom: 8, fontSize: 13, color: '#5f6368' }}>
        {company} — 예외처리 이력 (전체 {rows.length}건)
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e8eaed' }}>
              {th('department', '부서')}
              {th('name', '이름')}
              {th('ip', 'IP')}
              {th('exceptionItem', '예외항목')}
              {th('applyDate', '적용일')}
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>기간</th>
              {th('reason', '사유')}
              {th('approvalDocNo', '결재문서번호')}
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>비고</th>
              {th('actionType', '작업유형')}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24 }}>불러오는 중...</td></tr>
            ) : sorted.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24, color: '#888' }}>예외처리 이력이 없습니다.</td></tr>
            ) : sorted.map((row) => (
              <tr key={row.id} style={{ borderBottom: '1px solid #f1f3f4' }}>
                <td style={{ padding: '7px 10px' }}>{row.department || '-'}</td>
                <td style={{ padding: '7px 10px' }}>{row.name || '-'}</td>
                <td style={{ padding: '7px 10px', fontFamily: 'monospace' }}>{row.ip || '-'}</td>
                <td style={{ padding: '7px 10px' }}>{row.exceptionItem || '-'}</td>
                <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>{fmt(row.applyDate)}</td>
                <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                  {row.endDate ? `${fmt(row.startDate)} ~ ${fmt(row.endDate)}` : row.startDate ? `${fmt(row.startDate)} ~ 영구` : '-'}
                </td>
                <td style={{ padding: '7px 10px' }}>
                  <div style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.reason || ''}>{row.reason || '-'}</div>
                </td>
                <td style={{ padding: '7px 10px' }}>{row.approvalDocNo || '-'}</td>
                <td style={{ padding: '7px 10px' }}>
                  <div style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.remark || ''}>{row.remark || '-'}</div>
                </td>
                <td style={{ padding: '7px 10px' }}>{row.actionType || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default NacExceptionHistory;
